import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { ApiService } from '../../../services/api';
import { ProductImage, ProductImageCreateResponse, ProductImageReorderItem } from '../../../types';

interface UseProductImagesParams {
  chainId: number | '';
  upc: string;
  storeName?: string;
}

export function useProductImages({ chainId, upc, storeName }: UseProductImagesParams) {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();

  const [images, setImages] = useState<ProductImage[]>([]);
  const [loading, setLoading] = useState(false);

  // All images sorted by position
  const allImages = useMemo(() => [...images].sort((a, b) => a.position - b.position), [images]);

  // Filter to current chain, sort by position, and dedupe by imageUri
  // (keep earliest-position occurrence).
  const chainImages = useMemo(() => {
    if (!chainId) return [];
    const filtered = allImages.filter((img) => img.chainId === Number(chainId));
    const seen = new Set<string>();
    const deduped: ProductImage[] = [];
    for (const img of filtered) {
      if (seen.has(img.imageUri)) continue;
      seen.add(img.imageUri);
      deduped.push(img);
    }
    return deduped;
  }, [allImages, chainId]);

  const primaryImage = useMemo(() => chainImages[0] ?? null, [chainImages]);
  const alternativeImages = useMemo(() => chainImages.slice(1), [chainImages]);

  const fetchImages = useCallback(async () => {
    if (!token || !chainId || !upc) {
      setImages([]);
      return;
    }

    setLoading(true);
    try {
      const result = await ApiService.getProductImages(token, Number(chainId), upc, storeName);
      setImages(result);
    } catch (err) {
      console.error('Error fetching product images:', err);
    } finally {
      setLoading(false);
    }
  }, [token, chainId, upc, storeName]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const uploadImage = useCallback(
    async (
      imageFile: Blob,
      position: number = 0,
      source?: string
    ): Promise<ProductImageCreateResponse | null> => {
      if (!token || !chainId || !upc) return null;

      try {
        const result = await ApiService.uploadProductImage(
          token,
          Number(chainId),
          upc,
          imageFile,
          position,
          source
        );
        return result;
      } catch (err) {
        showErrorToast('Failed to upload product image');
        console.error('Error uploading product image:', err);
        return null;
      }
    },
    [token, chainId, upc, showErrorToast]
  );

  const deleteImage = useCallback(
    async (productImageId: number): Promise<boolean> => {
      if (!token) return false;

      const target = images.find((img) => img.productImageId === productImageId);
      const idsToDelete = target
        ? images
            .filter((img) => img.chainId === target.chainId && img.imageUri === target.imageUri)
            .map((img) => img.productImageId)
            .filter((id): id is number => id != null)
        : [productImageId];

      try {
        await Promise.all(idsToDelete.map((id) => ApiService.deleteProductImage(token, id)));
        return true;
      } catch (err) {
        showErrorToast('Failed to delete product image');
        console.error('Error deleting product image:', err);
        return false;
      }
    },
    [token, images, showErrorToast]
  );

  const reorderImages = useCallback(
    async (positions: ProductImageReorderItem[]): Promise<boolean> => {
      if (!token) return false;

      try {
        await ApiService.reorderProductImages(token, positions);
        showSuccessToast('Image order updated');
        return true;
      } catch (err) {
        showErrorToast('Failed to reorder images');
        console.error('Error reordering product images:', err);
        return false;
      }
    },
    [token, showErrorToast, showSuccessToast]
  );

  return {
    images: chainImages,
    allImages,
    primaryImage,
    alternativeImages,
    loading,
    refetch: fetchImages,
    uploadImage,
    deleteImage,
    reorderImages,
  };
}
